import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Container from "shared/components/Container";
import { NAME_SPACES } from "shared/locales/constants";
import Stepper from "../Components/Stepper";
import Loading from "../Components/Spin";

const View = ({ onNext, onBack, signatureLink }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const [isReady, setIsReady] = useState(false);
  const yousignInstanceRef = useRef(null);
  const scriptRef = useRef(null);

  const initializeYousign = useCallback(() => {
    // Clean up previous instance
    if (yousignInstanceRef.current) {
      yousignInstanceRef.current = null;
    }

    // Clear the iframe container
    const container = document.getElementById("iframe-container");
    if (container) {
      container.innerHTML = "";
    }

    // Check if script already exists
    if (scriptRef.current && document.body.contains(scriptRef.current)) {
      // Script already loaded, initialize directly
      if (window.Yousign) {
        yousignInstanceRef.current = new window.Yousign({
          signatureLink,
          iframeContainerId: "iframe-container",
          isSandbox: process.env.REACT_APP_YOUSIGN_SANDBOX === "true",
        });
        yousignInstanceRef.current.onSuccess((_) => onNext(_));
      }
      return;
    }

    // Load script if not already loaded
    const script = document.createElement("script");
    script.src = "https://cdn.yousign.tech/iframe-sdk-1.5.0.min.js";
    script.integrity =
      "sha384-wrNTU81R+b2K+9A1aE1c3blhReNpdRK6Tn1fzXOXhWGZGUk+Btp0dyfnk45pVpOr";
    script.crossOrigin = "anonymous";
    script.onload = () => {
      yousignInstanceRef.current = new window.Yousign({
        signatureLink,
        iframeContainerId: "iframe-container",
        isSandbox: process.env.REACT_APP_YOUSIGN_SANDBOX === "true",
      });
      yousignInstanceRef.current.onSuccess((_) => onNext(_));
    };
    scriptRef.current = script;
    document.body.appendChild(script);
  }, [signatureLink, onNext]);

  // Wait before initializing to ensure the signature link is ready on Yousign's side
  // This prevents showing the error page initially
  useEffect(() => {
    if (!signatureLink) return;

    // Wait 3 seconds before initializing the iframe to ensure the signature link is ready
    const initTimer = setTimeout(() => {
      setIsReady(true);
      initializeYousign();
    }, 3000);

    return () => {
      clearTimeout(initTimer);
      // Cleanup on unmount
      if (scriptRef.current && document.body.contains(scriptRef.current)) {
        // Don't remove script if it might be used elsewhere
        // Just clear the instance
        yousignInstanceRef.current = null;
      }
    };
  }, [signatureLink, initializeYousign]);

  // Show loading state while waiting for signature link to be ready
  if (!isReady) {
    return <Loading />;
  }

  return (
    <Container size={"medium"}>
      <div className="b2c__area">
        <Stepper
          items={[
            { label: TRANSLATION.STTEPER.SITUATION, completed: true },
            { label: TRANSLATION.STTEPER.QUOTE, completed: true },
            { label: TRANSLATION.STTEPER.SUBSCRIPTION, completed: true },
            { label: TRANSLATION.STTEPER.VALIDATION, completed: true },
          ]}
        />
        <div className="b2c__area--header">
          <div className="b2c__area--header__title">
            {TRANSLATION.ELECTRONIC_SIGNATURE.TITLE}
          </div>
          <div className="b2c__area--header__subtitle">
            {TRANSLATION.ELECTRONIC_SIGNATURE.SUBTITLE}
          </div>
        </div>
        <div id="iframe-container"></div>
      </div>
    </Container>
  );
};

export default View;
